/**
 * AI 每日追蹤代辦 設定 schema — REQ-20260419007。
 * DB key='ai.followup'。lastRun 內嵌存於同一 JSON,給 UI 顯示「上次執行」資訊。
 */
import { z } from 'zod';

export const AI_FOLLOWUP_TIMEZONES = [
  'Asia/Taipei',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'UTC',
] as const;

export const AI_FOLLOWUP_RUN_STATUSES = [
  'ok',
  'no_provider',
  'no_telegram',
  'cost_cap',
  'no_candidates',
  'llm_error',
  'tg_error',
  'unknown_error',
] as const;

export const AiFollowupLastRunSchema = z.object({
  runAt: z.string().datetime(),
  status: z.enum(AI_FOLLOWUP_RUN_STATUSES),
  candidates: z.number().int(),
  picked: z.number().int(),
  tokensIn: z.number().int(),
  tokensOut: z.number().int(),
  costUsd: z.number(),
  manual: z.boolean().default(false),
  error: z.string().nullable(),
  /** 最近一次發送到 Telegram 的訊息內容(供 UI 預覽用;有錯誤時可能為 null) */
  messagePreview: z.string().nullable().optional(),
});
export type AiFollowupLastRun = z.infer<typeof AiFollowupLastRunSchema>;

export const AiFollowupSettingInputSchema = z.object({
  enabled: z.boolean().default(false),
  notifyTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, '請輸入 HH:mm,例 09:00')
    .default('09:00'),
  timezone: z.enum(AI_FOLLOWUP_TIMEZONES).default('Asia/Taipei'),
  staleDays: z.coerce.number().int().min(1).max(365).default(7),
  interviewTypeIds: z.array(z.coerce.number().int().positive()).default([]),
  maxItems: z.coerce.number().int().min(1).max(50).default(10),
  promptTemplate: z.string().max(8000).default(''),
});
export type AiFollowupSettingInput = z.infer<typeof AiFollowupSettingInputSchema>;

export const AiFollowupSettingViewSchema = AiFollowupSettingInputSchema.extend({
  updatedAt: z.string().datetime().nullable(),
  lastRun: AiFollowupLastRunSchema.nullable(),
});
export type AiFollowupSettingView = z.infer<typeof AiFollowupSettingViewSchema>;

export const AiFollowupRunResultSchema = z.object({
  status: z.enum(AI_FOLLOWUP_RUN_STATUSES),
  candidates: z.number().int(),
  picked: z.number().int(),
  message: z.string(),
});
export type AiFollowupRunResult = z.infer<typeof AiFollowupRunResultSchema>;

/** 預設 prompt 範本（供前端帶入 placeholder 與後端 fallback） */
export const AI_FOLLOWUP_DEFAULT_PROMPT = `你是一位客戶關係管理助理。以下是近期未接觸的候選客戶清單(JSON),
請從中挑出最需要今天優先追蹤的 {{maxItems}} 筆,並針對每筆產出:
- priority: high / normal
- reason: 為何需要追蹤(一句話,繁體中文,<= 60 字)
- suggestedAction: 建議的下一步動作(一句話,繁體中文,<= 60 字)

候選清單:
{{customers}}

僅輸出符合 schema 的 JSON,不要額外說明。`;
